import { HTTPError } from 'ky';
import { http } from '@/lib/http';
import { notePriceRefusal } from './price-refusal';
import type {
  LccCandidateRequest,
  LccCandidateResult,
  LccOffer,
  LccProvider,
  LccPurchase,
  LccPurchaseRequest,
  LccSearch,
} from './types';

const base = 'v1/rebooking/lcc';
const options = { timeout: 155_000, retry: 0 } as const;
const responseCodes = new WeakMap<Response, string>();

function safeCode(value: unknown): string | undefined {
  return typeof value === 'string' && /^[A-Z][A-Z0-9_]{0,80}$/.test(value) ? value : undefined;
}

// The shared client's beforeError hook consumes the body. Preserve only the
// sanitized code before that hook runs so definitive preflight rejections can
// require a fresh review without being mistaken for an unknown purchase.
const lccHttp = http.extend({
  hooks: {
    afterResponse: [
      async (_request, _options, response) => {
        if (!response.ok) {
          try {
            const body = (await response.clone().json()) as { code?: unknown };
            const code = notePriceRefusal(response, body) ?? safeCode(body?.code);
            if (code) responseCodes.set(response, code);
          } catch {
            /* Invalid response bodies remain an unknown failure. */
          }
        }
        return response;
      },
    ],
  },
});

export const lccApi = {
  providers: () =>
    lccHttp
      .get(`${base}/providers`, options)
      .json<{ providers: LccProvider[]; externalPurchaseOnly?: boolean }>(),
  candidates: (body: LccCandidateRequest) =>
    lccHttp.post(`${base}/candidates`, { ...options, json: body }).json<LccCandidateResult>(),
  search: (body: LccSearch) =>
    lccHttp
      .post(`${base}/search`, { ...options, json: body })
      .json<{ offers: LccOffer[]; errors: Array<{ provider: string; code: string }> }>(),
  refresh: (id: string) =>
    lccHttp
      .post(`${base}/offers/${encodeURIComponent(id)}/refresh`, { ...options, json: {} })
      .json<LccOffer>(),
  purchases: (caseUrn: string, groupId: string) =>
    lccHttp
      .get(`${base}/purchases`, { ...options, searchParams: { caseUrn, groupId } })
      .json<{ purchases: LccPurchase[] }>(),
  purchase: (body: LccPurchaseRequest, key: string) =>
    lccHttp
      .post(`${base}/purchases`, { ...options, json: body, headers: { 'Idempotency-Key': key } })
      .json<LccPurchase>(),
  reconcile: (id: string) =>
    lccHttp
      .post(`${base}/purchases/${encodeURIComponent(id)}/reconcile`, { ...options, json: {} })
      .json<LccPurchase>(),
};

/** Never display supplier messages: they can echo sensitive request fields. */
export async function lccErrorCode(error: unknown): Promise<string> {
  if (error instanceof HTTPError) {
    const captured = responseCodes.get(error.response);
    if (captured) return captured;
    try {
      const body = (await error.response.clone().json()) as { code?: unknown };
      const code = notePriceRefusal(error.response, body) ?? safeCode(body?.code);
      if (code) return code;
    } catch {
      /* Only a stable code is safe to show. */
    }
  }
  return 'LCC_UNAVAILABLE';
}
